import { Book } from '../types/book';
import { SearchResult, SearchIndex } from '../types/search';

class SearchService {
  private searchIndex: SearchIndex[] = [];

  // Build search index from book content
  buildIndex(book: Book): void {
    this.searchIndex = [];
    let totalSections = 0;
    let sectionsWithContent = 0;
    
    book.chapters.forEach(chapter => {
      chapter.sections.forEach(section => {
        totalSections++;
        
        // Skip sections with no content
        if (!section.content || section.content.trim().length === 0) {
          console.warn(`Section ${section.id} has no content, skipping from search index`);
          return;
        }
        
        sectionsWithContent++;
        
        // Clean content for searching (remove markdown, extra whitespace)
        const cleanContent = this.cleanTextForSearch(section.content);
        
        this.searchIndex.push({
          sectionId: section.id,
          chapterId: chapter.id,
          chapterTitle: chapter.title,
          sectionTitle: section.title,
          content: section.content,
          searchableText: cleanContent
        });
      });
    });
    
    console.log(`Search index built: ${this.searchIndex.length} sections indexed out of ${totalSections} total sections (${sectionsWithContent} had content)`);
    
    // Log a sample of what's in the index for debugging
    if (this.searchIndex.length > 0) {
      const sample = this.searchIndex[0];
      console.log(`Sample indexed content from ${sample.sectionId}:`, sample.searchableText.substring(0, 200) + '...');
    }
  }

  // Clean text for better searching
  private cleanTextForSearch(content: string): string {
    return content
      // Remove markdown syntax
      .replace(/#{1,6}\s+/g, '') // headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // bold
      .replace(/\*(.*?)\*/g, '$1') // italic
      .replace(/`(.*?)`/g, '$1') // inline code
      .replace(/```[\s\S]*?```/g, '') // code blocks
      .replace(/!\[.*?\]\(.*?\)/g, '') // images
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
      .replace(/\|.*?\|/g, '') // tables
      .replace(/>\s+/g, '') // blockquotes
      .replace(/[-*+]\s+/g, '') // list markers
      .replace(/\d+\.\s+/g, '') // numbered lists
      // Clean up whitespace
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  // Search for text and return results
  search(query: string, maxResults: number = 20): SearchResult[] {
    if (!query.trim() || this.searchIndex.length === 0) {
      return [];
    }

    const searchTerm = query.toLowerCase().trim();
    const results: SearchResult[] = [];

    for (const item of this.searchIndex) {
      const matchIndex = item.searchableText.indexOf(searchTerm);
      
      if (matchIndex !== -1) {
        // Extract context around the match
        const contextPreview = this.extractContext(item.searchableText, matchIndex, searchTerm.length);
        
        // Find the actual match in the original content for display
        const originalMatchIndex = item.content.toLowerCase().indexOf(searchTerm);
        const matchText = originalMatchIndex !== -1 
          ? item.content.substring(originalMatchIndex, originalMatchIndex + searchTerm.length)
          : searchTerm;

        results.push({
          sectionId: item.sectionId,
          chapterId: item.chapterId,
          chapterTitle: item.chapterTitle,
          sectionTitle: item.sectionTitle,
          matchText,
          contextPreview,
          matchIndex
        });

        // Stop when we reach the max results
        if (results.length >= maxResults) {
          break;
        }
      }
    }

    return results;
  }

  // Extract context around a match
  private extractContext(text: string, matchIndex: number, matchLength: number, contextLength: number = 100): string {
    const start = Math.max(0, matchIndex - contextLength / 2);
    const end = Math.min(text.length, matchIndex + matchLength + contextLength / 2);
    
    let context = text.substring(start, end);
    
    // Add ellipsis if we're not at the beginning/end
    if (start > 0) {
      context = '...' + context;
    }
    if (end < text.length) {
      context = context + '...';
    }
    
    return context;
  }

  // Get the current search index size
  getIndexSize(): number {
    return this.searchIndex.length;
  }

  // Check if index is built
  isIndexReady(): boolean {
    return this.searchIndex.length > 0;
  }
}

// Export singleton instance
export const searchService = new SearchService();
